<template>
    <div :class="{'dataview-chart' : true,'loading-graph': isLoading }">
      <template v-if="graphIsEmpty && !filters">
          No data available.
      </template>
      <template v-else>
        <div class="grid-25w filters">
            <treeselect v-if="filters"
                        v-for="filter in filters"
                        :key="filter.instanceId"
                        v-model="filterValues[filter.instanceId]"
                        :multiple="isMultiple(filter)"
                        :clearable="isMultiple(filter)"
                        :options="filter.values"
                        :placeholder="filter.name"
                        :instanceId="filter.instanceId"
                        @close="filterChanged"
                        @input="filterChanged" />
            <div v-if="filters && filters.length === 2"></div>
            <div v-if="buttonLoad" style="float:right">
                <button style="margin-top:1px;width:100%;padding-top:4px; padding-bottom:3px;" v-on:click="getChartByClick" >
                    Load chart
                    <div style="font-size:8px; text-transform:none; text-align:center;">(Use filters for faster load)</div>
                </button>
                
            </div>
        </div>
        <div class="grid-25w kpis" v-if="kpis">
            <div v-for="kpi in kpis">
                <div class="kpi-title">
                    <h4 :class="{ 'with-helper': kpi.helper }">{{ kpi.name }}</h4>
                    <HelperBox v-if="kpi.helper" :text="kpi.helper"></HelperBox>
                </div>
                <span :class="{ 'with-helper': kpi.helper }">{{ kpi.value }}</span>

            </div>
        </div>
        <div class="grid-full">
            <template v-if="isGantt">
                <highcharts v-if="chart"
                            :options="chart"
                            :constructor-type="'ganttChart'"
                            :key="rerenderKey">
                </highcharts>
            </template>
            <template v-else>
                <div style="min-height: 400px;width: 100%;min-width: 100%;">
                    <div v-if="!autoLoadChart" style="font-size:16px;text-align:center;line-height:400px;">
                        Please use filters before pressing load to see chart.
                    </div>
                    <div v-else >
                        <template v-if="graphIsEmpty && filters">
                          <div>No data with selected combination</div>
                        </template>
                        <highcharts v-if="chart"
                                    :options="chart"
                                    :key="rerenderKey">
                        </highcharts>
                    </div>
                </div>
            </template>
            <p v-if="loadingFailed">
                Could not load chart. Please review input data.
            </p>
        </div>
      </template>
    </div>
  
</template>

<script>
    import { Chart } from "highcharts-vue"
    import Highcharts from "highcharts"
    import Treeselect from "@riophae/vue-treeselect"
    import ganttInit from "highcharts/modules/gantt"
    import exportingInit from "highcharts/modules/exporting"
    import exportingOfflineInit from "highcharts/modules/offline-exporting"
    import HelperBox from "../../HelperBox.vue"
    import API from "../../../../Api"

    import "@riophae/vue-treeselect/dist/vue-treeselect.css"

    Highcharts.setOptions({
        lang: {
            numericSymbols: null,
            decimalPoint: ".",
            thousandsSep: ","
        }
    });

    ganttInit(Highcharts);
    exportingInit(Highcharts);
    exportingOfflineInit(Highcharts);

    export default {
        components: {
            Treeselect,
            highcharts: Chart,
            HelperBox
        },
        props: {  
            settings: {
                type: Function,
                required: true
            },
            //when referring, remember to use ex: v-bind:buttonLoad="false"
            buttonLoad: {
                type: Boolean,
                required: false
            },
            chartName: {
                type: String,
                required: true
            },
            isGantt: {
                type: Boolean,
                required: false
            },
            multiple: {
                type: [Boolean, Object],
                required: false,
                default: true
            },
            isAggregation: {
                type: [Boolean, Object],
                required: false,
                default: false
            }
            
        },
        data: () => ({
            chart: null,
            filters: null,
            filterValues: {},
            categories: null,
            kpis: null,
            isLoading: true,
            loadingFailed: false,
            cancelToken: null,
            rerenderKey: true,
            autoLoadChart: true,
            graphIsEmpty: false
        }),
        mounted() {
            this.$el.addEventListener('fullscreenchange', this.fullScreenChanged)
            this.isLoading = false;
            
            var filtersOnly = this.buttonLoad;
            
            if (filtersOnly)
                this.autoLoadChart = false;

            this.getChart(filtersOnly).then(() => {
                    if ((typeof (this.multiple) === "boolean" && !this.multiple) || this.multiple instanceof Object) {
                        return this.getChart(filtersOnly)
                    }
                });
            
        },
        beforeDestroy: function () {
            this.$el.removeEventListener('fullscreenchange', this.fullScreenChanged)

            // Cancel all ongoing requests if user is changing page
            if (this.cancelToken)
                this.cancelToken.cancel();
        },
        methods: {
            fullScreenChanged() {
                if (!document.fullscreenElement) {
                    this.rerenderKey = !this.rerenderKey
                }
            },
            isMultiple(filter) {
                if (typeof (this.multiple) === "boolean") {
                    return this.multiple;
                }

                if (this.multiple instanceof Object) {
                    const val = this.multiple[filter.name]
                    if (typeof (val) === "boolean") {
                        return val;
                    }
                }
                
                return true;
            },     
            // event handler for filters
            filterChanged() {
                this.getChart(this.buttonLoad);                                
            }
            ,
            getChartByClick() {                 
                this.getChart(false);                
                this.autoLoadChart = true;
            },            
            getChart(onlyFilters) {
                this.isLoading = true;

                let url = this.isAggregation ? "dataviewaggregation/getchart?name=" + this.chartName + "&id=" + this.$route.params.id + "&onlyFilters=" + onlyFilters : "dataview/getchart?name=" + this.chartName + "&id=" + this.$route.params.id + "&onlyFilters=" + onlyFilters;

                const filterKeys = Object
                    .keys(this.filterValues)
                    .filter(key => key)
                    .map(key => { return { key: key, value: this.filterValues[key] } })
                    .filter(x => x.value && x.value.length > 0);

                if (filterKeys.length > 0) {
                    const filters = {};
                    filterKeys.forEach(x => {
                        // force filter into canonical form ie. an array of chosen filters
                        if (Array.isArray(x.value))
                            filters[x.key] = x.value;
                        else
                            // this happens when multiple: false. We force the single value into an array
                            filters[x.key] = [x.value];
                    })

                    url += "&filters=" + JSON.stringify(filters)
                }

                this.cancelToken = API.CancelToken.source();

                return API.get(url, {
                    cancelToken: this.cancelToken.token
                }).then((res) => {

                    //debugger;
                    this.filters = res.data.filters;

                    if (this.filters !== null) {
                        this.filters.forEach(filter => {
                            if (!(this.isMultiple(filter) || this.filterValues[filter.instanceId])) {
                                if (filter.values.length > 0) {
                                    this.filterValues[filter.instanceId] = filter.values[0].id;
                                }
                            }
                        });
                    }

                    this.categories = res.data.categories;
                    this.kpis = res.data.kpis;

                    if (!onlyFilters) {
                        if (this.isGantt) {
                            // Gant requires a full rerender due to using a constructor
                            this.chart = null;
                            this.$nextTick(() => {
                                this.chart = this.settings(res.data);
                            });
                        } else {
                            this.chart = this.settings(res.data);
                        }
                    }
                    
                    if (!onlyFilters)
                    {
                        let isEmpty = false;
                        
                        if (res.data.series == null || res.data.series.length === 0)
                        {
                            isEmpty = true;
                        }
                        else
                        {
                            isEmpty = false;
                            for (let i=0; i<res.data.series.length; i++)
                            {
                                  if (res.data.series[i].data === null || res.data.series[i].data === undefined)
                                    continue;

                                  if (res.data.series[i].data.length > 0)
                                    break;

                                  isEmpty = true;
                            }
                        }
                      
                        this.graphIsEmpty = isEmpty;
                    }
                    
                    
                }).catch(() => {
                    this.loadingFailed = true;
                }).finally(() => {
                    this.isLoading = false;
                    window.dispatchEvent(new Event('resize'));
                });
            }
        }
    }
</script>
